import React, { useState } from 'react';
import './CustomLoginPage.css';
import { GoogleLogin } from '@react-oauth/google';
import { Link2, MailCheck } from 'lucide-react';

type LoginPageMode = 'login' | 'signup';

interface CustomLoginPageProps {
    loginHelper: any;
    onLoginSuccess: () => void;
    onLoginError: (error: string) => void;
    initialMode?: LoginPageMode;
}

/**
 * The sign-in panel: Google, or a link emailed to you. There is no password to remember, and no
 * separate sign-up: an address that has never signed in simply starts an account.
 */
const CustomLoginPage: React.FC<CustomLoginPageProps> = ({
    loginHelper,
    onLoginSuccess,
    onLoginError,
    initialMode = 'login',
}) => {
    const [email, setEmail] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [error, setError] = useState('');

    const handleGoogleSuccess = async (credentialResponse: any) => {
        setError('');
        try {
            await loginHelper.signInWithGoogleIdToken(credentialResponse.credential);
            onLoginSuccess();
        } catch (err: any) {
            const message = err?.message || 'Google sign-in failed. Please try again.';
            setError(message);
            onLoginError(message);
        }
    };

    const handleSendLink = async (event: React.FormEvent) => {
        event.preventDefault();
        const address = email.trim();
        if (!address) return;
        setIsSending(true);
        setError('');
        try {
            await loginHelper.requestSignInLink(address);
            setSentTo(address);
        } catch (err: any) {
            const message = err?.message || 'We could not send a link just now. Try again in a minute.';
            setError(message);
            onLoginError(message);
        } finally {
            setIsSending(false);
        }
    };

    if (sentTo) {
        return (
            <div className="login-page-container">
                <div className="login-content-container">
                    <div className="login-header">
                        <MailCheck size={32} strokeWidth={1.5} aria-hidden />
                        <h2 className="login-title">Check your inbox</h2>
                        <p className="login-subtitle">
                            We sent a sign-in link to <b>{sentTo}</b>.
                        </p>
                    </div>
                    <div className="login-form-container">
                        <button
                            type="button"
                            className="login-link-button"
                            onClick={() => { setSentTo(null); setError(''); }}
                        >
                            Use a different email
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page-container">
            <div className="login-content-container">
                <div className="login-header">
                    <h2 className="login-title">
                        {initialMode === 'signup' ? 'Create your account' : 'Sign in to HomeIndex'}
                    </h2>
                </div>

                <div className="login-form-container">
                    {error && <div className="login-error-message" role="alert">{error}</div>}

                    <div className="login-google-button">
                        <span className="login-google-face" aria-hidden>
                            <svg width="18" height="18" viewBox="0 0 48 48">
                                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.8 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.1C12.4 13.6 17.7 9.5 24 9.5z"/>
                                <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.1 5.3-4.5 7l7.3 5.7c4.3-4 6.9-9.9 6.9-17.2z"/>
                                <path fill="#FBBC05" d="M10.5 28.7a14.5 14.5 0 0 1 0-9.4l-7.9-6.1a24 24 0 0 0 0 21.6l7.9-6.1z"/>
                                <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.3-5.7c-2 1.4-4.9 2.3-8.6 2.3-6.3 0-11.6-4.1-13.5-9.8l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
                            </svg>
                            Continue with Google
                        </span>
                        <div className="login-google-hit">
                            <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={() => setError('Google sign-in failed. Please try again.')}
                                width={400}
                                text="continue_with"
                            />
                        </div>
                    </div>

                    <div className="login-divider"><span>or</span></div>

                    <form onSubmit={handleSendLink} className="login-form">
                        <div className="login-form-group">
                            <input
                                type="email"
                                id="login-email"
                                aria-label="Email address"
                                name="email"
                                autoComplete="email"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="login-form-input"
                                required
                            />
                        </div>
                        <button type="submit" disabled={isSending || !email.trim()} className="login-primary-button">
                            {isSending ? 'Sending…' : (<><Link2 size={18} strokeWidth={2} aria-hidden />Send sign in link</>)}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CustomLoginPage;
